import {
  Box,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import React from "react";

type Column<T> = {
  label: string;
  render: (item: T) => React.ReactNode;
  sx?: React.CSSProperties | object; // Allow passing style for width etc.
};

type Props<T> = {
  columns: Column<T>[];
  data: T[];
  isLoading: boolean;
  emptyMessage?: string;
  onRowClick?: (item: T) => void;
  isSelected?: (item: T) => boolean;
  rowHeight?: number;
  aggregates?: { label: string; value: React.ReactNode; columnName: string }[];
};

const SelectableTable = <T,>({
  columns,
  data,
  isLoading = false,
  emptyMessage = "Keine Daten gefunden.",
  onRowClick,
  isSelected,
  rowHeight = 56,
  aggregates,
}: Props<T>) => {
  return (
    <Table stickyHeader sx={{ tableLayout: "fixed" }}>
      <TableHead>
        <TableRow>
          {columns.map((col, idx) => (
            <TableCell key={idx} sx={{ fontSize: "1rem", ...col.sx }}>
              {col.label}
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {isLoading ? (
          <TableRow>
            <TableCell colSpan={columns.length} align="center">
              <CircularProgress />
            </TableCell>
          </TableRow>
        ) : data.length === 0 ? (
          <TableRow>
            <TableCell colSpan={columns.length} align="center">
              {emptyMessage}
            </TableCell>
          </TableRow>
        ) : (
          data.map((item, index) => {
            const selected = isSelected?.(item);
            return (
              <TableRow
                key={index}
                onClick={() => onRowClick?.(item)}
                sx={{
                  cursor: onRowClick ? "pointer" : "default",
                  backgroundColor: selected ? "secondary.light" : undefined,
                  "&:hover": {
                    backgroundColor: selected ? "secondary.main" : "grey.100",
                  },
                  height: rowHeight,
                }}
              >
                {columns.map((col, colIdx) => (
                  <TableCell key={colIdx} sx={{ fontSize: "1rem", ...col.sx }}>
                    {col.render(item)}
                  </TableCell>
                ))}
              </TableRow>
            );
          })
        )}
        {/* Add the aggregates row dynamically */}
        {aggregates && (
          <TableRow>
            {columns.map((col, idx) => {
              const aggregate = aggregates.find((agg) => agg.columnName === col.label);
              return (
                <TableCell key={idx} sx={{ fontWeight: "bold", textAlign: "left", ...col.sx }}>
                  {aggregate ? (
                    <>
                      {aggregate.label}{" "}
                      <span style={{ fontWeight: "normal" }}>{aggregate.value}</span>
                    </>
                  ) : (
                    " "
                  )}
                </TableCell>
              );
            })}
          </TableRow>
        )}
      </TableBody>
    </Table>
  );
};

export default SelectableTable;
